import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { WebStorageStateStore } from 'oidc-client-ts'
import { type PropsWithChildren, useEffect, useRef, useState } from 'react'
import { AuthProvider, useAuth } from 'react-oidc-context'
import type { GatewayDefinition } from '../configuration/gateways'

interface OidcConfig {
  url: string
  clientId: string
}

type ConfigState =
  | { status: 'loading' }
  | { status: 'ready'; config: OidcConfig }
  | { status: 'error'; message: string }

function parseOidcConfig(value: unknown): OidcConfig | undefined {
  if (!value || typeof value !== 'object') return undefined
  const config = value as Record<string, unknown>
  if (typeof config.url !== 'string' || typeof config.clientId !== 'string') return undefined
  return { url: config.url, clientId: config.clientId }
}

export function VulcanAuthProvider({ children, gateway }: PropsWithChildren<{ gateway: GatewayDefinition }>) {
  const [configState, setConfigState] = useState<ConfigState>({ status: 'loading' })
  const [retry, setRetry] = useState(0)

  useEffect(() => {
    void retry
    const controller = new AbortController()
    setConfigState({ status: 'loading' })
    async function loadConfig() {
      try {
        const response = await fetch(`${gateway.baseUrl}/oidc`, { signal: controller.signal })
        if (!response.ok) throw new Error(`The federated gateway returned HTTP ${response.status}.`)
        const config = parseOidcConfig(await response.json())
        if (!config) throw new Error('The federated gateway returned an invalid OIDC configuration.')
        if (!controller.signal.aborted) setConfigState({ status: 'ready', config })
      } catch (error) {
        if (!controller.signal.aborted) {
          setConfigState({
            status: 'error',
            message: error instanceof Error ? error.message : 'Unable to load the OIDC configuration.'
          })
        }
      }
    }
    void loadConfig()
    return () => controller.abort()
  }, [gateway.baseUrl, retry])

  if (configState.status === 'loading') return <CenteredProgress label={`Connecting to ${gateway.label}…`} />
  if (configState.status === 'error') {
    return (
      <CenteredCard>
        <Alert severity="error" action={<Button onClick={() => setRetry((value) => value + 1)}>Retry</Button>}>
          <Typography sx={{ fontWeight: 700 }}>Could not connect to {gateway.label}</Typography>
          Check your connection and try again. If the problem continues, check that this gateway is available.
        </Alert>
      </CenteredCard>
    )
  }

  return <AuthProvider {...createAuthProviderProps(configState.config, gateway.baseUrl)}>{children}</AuthProvider>
}

export function AuthenticatedVulcan({ children }: PropsWithChildren) {
  const auth = useAuth()
  const signInStarted = useRef(false)

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated && !auth.activeNavigator && !auth.error && !signInStarted.current) {
      signInStarted.current = true
      sessionStorage.setItem(
        'vulcan-return-path',
        window.location.pathname + window.location.search + window.location.hash
      )
      void auth.signinRedirect().catch(() => undefined)
    }
  }, [auth])

  if (auth.isLoading || auth.activeNavigator === 'signinRedirect') {
    return <CenteredProgress label="Signing in to Vulcan…" />
  }
  if (auth.error) {
    return (
      <CenteredCard>
        <Alert
          severity="error"
          action={<Button onClick={() => void auth.signinRedirect().catch(() => undefined)}>Try again</Button>}
        >
          <Typography sx={{ fontWeight: 700 }}>Sign-in failed</Typography>
          Try signing in again. If the problem continues, contact your administrator.
        </Alert>
      </CenteredCard>
    )
  }
  if (!auth.isAuthenticated) return <CenteredProgress label="Redirecting to sign in…" />
  return <>{children}</>
}

export function createAuthProviderProps(config: OidcConfig, baseUrl: string) {
  const prefix = `vulcan.oidc.${encodeURIComponent(baseUrl.replace(/\/+$/, ''))}.`
  return {
    authority: config.url,
    client_id: config.clientId,
    redirect_uri: `${window.origin}/graphiql/global`,
    post_logout_redirect_uri: `${window.origin}/graphiql/global`,
    response_type: 'code' as const,
    scope: 'openid email',
    userStore: new WebStorageStateStore({ store: window.localStorage, prefix }),
    stateStore: new WebStorageStateStore({ store: window.sessionStorage, prefix }),
    onSigninCallback: () => {
      const returnPath = sessionStorage.getItem('vulcan-return-path') ?? '/'
      sessionStorage.removeItem('vulcan-return-path')
      window.history.replaceState({}, document.title, returnPath)
    }
  }
}

function CenteredProgress({ label }: { label: string }) {
  return (
    <Box sx={{ display: 'grid', minHeight: '100dvh', placeItems: 'center' }}>
      <Box sx={{ display: 'grid', justifyItems: 'center', gap: 2 }}>
        <CircularProgress size={32} />
        <Typography color="text.secondary">{label}</Typography>
      </Box>
    </Box>
  )
}

function CenteredCard({ children }: PropsWithChildren) {
  return (
    <Box sx={{ display: 'grid', minHeight: '100dvh', placeItems: 'center', p: 3 }}>
      <Box sx={{ width: '100%', maxWidth: 620 }}>{children}</Box>
    </Box>
  )
}
